import { Component, inject, Input } from '@angular/core';

import { AufnahmePunktDocument, LastModified } from '../../data-model';
import {
	CheckerComments, commentTimeString, CommentsModalComponent, displayString,
	ValidationLevel as ValRes,
	PointStatus, pointStatusBadge, statusBadge, StatusBadge,
	transitionButtonClass, transitionButtonLabel,
	WorkflowService, WorkflowTransitionModalComponent, WorkflowDiagramModalComponent,
	hasUnresolvedDangerComments, ValidationLevel, isOwnPoint
} from '@luzula/luzula-lib';
import { ValidationService } from '../validation.service';
import { AuthService } from 'src/app/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProjectService } from 'src/app/project.service';
import { ToastrService } from 'ngx-toastr';
import { DataService } from 'src/app/data.service';
import { foreignProjectOf } from './point-project-membership';
import { updateAccordingly } from './bohrpunkt-detail.resolver';
import { PointFreigabeService } from './point-freigabe.service';
import { shouldHideProjectName } from './project-name-visibility';
import { personenangabenVerbergen } from '../personenangaben';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-plausi',
    templateUrl: './bohrpunkt-plausi.component.html',
    styleUrls: ['./bohrpunkt-plausi.component.css'],
    
})
export class BohrpunktPlausiComponent {
	@Input() bp!: AufnahmePunktDocument;

	ValRes = ValRes;
	displayString = displayString;
	commentTimeString = commentTimeString;

	// Collapse-Status
	messagesCollapsed = false;
	commentsCollapsed = false;
	workflowCollapsed = false;

	get hasWorkflow(): boolean {
		return !!this.bp?.workflow;
	}

	/**
	 * Workflow-Sektion auch für Bestandsdaten (workflow === undefined) zeigen,
	 * damit „In Bearbeitung nehmen" + Diagramm dort erreichbar bleiben — früher
	 * nur über den (entfernten) Header-Knopf möglich.
	 */
	get showWorkflowSection(): boolean {
		return this.hasWorkflow || this.bp?.workflow === undefined;
	}

	get workflowHistoryCount(): number {
		const wf = this.bp?.workflow;
		if (!wf) return 0;
		// Anlege-/Übernahme-Eintrag (from===null) wird als Herkunfts-Zeile gerendert,
		// nicht in der Liste.
		const history = wf.history ?? [];
		const transitions = history.filter(e => e.kind !== 'reset' && (e as any).from !== null).length;
		const extras = (wf.rejectionComment ? 1 : 0) + (wf.approvalComment ? 1 : 0);
		return transitions + extras;
	}

	private modalService = inject(NgbModal);
	private aus = inject(AuthService);
	ps = inject(ProjectService);
	vs = inject(ValidationService);
	private workflow = inject(WorkflowService);
	private toastr = inject(ToastrService);
	private ds = inject(DataService);
	private freigabe = inject(PointFreigabeService);

	/**
	 * Nach einem erfolgreichen Status-Wechsel hier im Hinweise-Tab den geänderten
	 * Punkt über `pointSaved` melden.
	 *
	 * Der Status wird vom Workflow-System der Lib direkt geschrieben (`data.put`),
	 * NICHT über `DataService.saveDocument` — d.h. ohne dieses explizite Signal
	 * bekommen Cache-Hörer den Wechsel nicht mit. Konkret cacht `HlAreaDirective`
	 * ([bpHl]) ihre „darf ich editieren?"-Entscheidung und verwirft den Cache nur
	 * auf `pointSaved`; sonst bleiben die anklickbaren Protokoll-Bereiche nach z.B.
	 * „Punkt zurück in bearbeitung" gesperrt, bis der Punkt verlassen + neu
	 * geöffnet wird. Reiner Refresh — die Rechte-Regeln bleiben unberührt.
	 */
	private notifyPointChanged(): void {
		this.ds.pointSaved.emit(this.bp);
	}

	// === Workflow-Status + Aktions-Buttons (jetzt im Hinweise-Tab) ===

	get currentStatus(): PointStatus | null {
		return this.bp?.workflow?.status ?? null;
	}

	get currentBadge(): StatusBadge | null {
		// pointStatusBadge mit Workflow-Block (statt nur Status): erkennt auch
		// V11→V12-Migrations-Bestand (status='abgeschlossen' + createdBy.id='system'
		// + History [from=null,to='abgeschlossen']) als „Bestand".
		return pointStatusBadge(this.bp?.workflow);
	}

	statusBadgeFor = (status: PointStatus | null) => statusBadge(status);
	// Outline-Variante: Aktions-Buttons (Status ändern) heben sich so vom
	// gefüllten Status-Badge (aktueller Status) ab.
	transitionButtonClass = (target: PointStatus) => transitionButtonClass(target, true);
	transitionButtonLabel = (from: PointStatus | null, target: PointStatus) =>
		transitionButtonLabel(from, target);

	get availableTransitions(): PointStatus[] {
		if (!this.bp) return [];
		// Status-Wechsel nur für Punkte, die dem aktiven Projekt zugeordnet sind —
		// symmetrisch zum Feld-Editier-Gate der HlAreaDirective. Sonst könnte ein
		// Owner aus einem (leeren) Projekt heraus den Status fremder Punkte ändern.
		if (!this.isAssignedToActiveProject()) return [];
		return this.workflow.availableTransitions(this.bp);
	}

	/**
	 * Kontrolleur-Reopen (siehe `BohrpunktTabsComponent.canReopen`): Knopf
	 * „Zurück in die Kontrolle" erscheint im Hinweise-Tab neben den anderen
	 * Workflow-Aktionen, sobald der Punkt `abgeschlossen` oder `abgelehnt` ist
	 * (User-Wunsch 2026-06-01). Berechtigung: `ProjectService.canIReopenPoint`.
	 */
	get canReopen(): boolean {
		if (!this.bp) return false;
		return this.ps.canIReopenPoint(this.bp);
	}

	async onReopen(): Promise<void> {
		const result = await this.workflow.reopenPointToReview(this.bp);
		if (result.ok && result.doc) {
			Object.assign(this.bp, result.doc);
			this.notifyPointChanged();
			this.toastr.success(`Neuer Status: ${this.currentBadge?.label ?? 'in_pruefung'}`);
		} else if (result.reason) {
			this.toastr.error(result.reason);
		}
	}

	/**
	 * „Übernahme zurücknehmen" (User-Wunsch 2026-06-19): Umkehrung der Übernahme.
	 * Ein per Übernahme (abgeschlossen → anstehend) ins aktive Projekt geholter
	 * Punkt wird wieder auf „abgeschlossen" gesetzt und dem vorigen Projekt
	 * zurückgegeben (oder Bestand, falls es das Projekt nicht mehr gibt).
	 * Berechtigung identisch zur Übernahme (`canIzuruecknehmenPoint` →
	 * Manager/Entwickler). Knopf sitzt neben „Zurück in die Kontrolle".
	 */
	get canZuruecknehmen(): boolean {
		if (!this.bp) return false;
		return this.ps.canIzuruecknehmenPoint(this.bp);
	}

	async onZuruecknehmen(): Promise<void> {
		// Der Service toastet Erfolg/Fehler selbst (analog freigeben).
		const result = await this.freigabe.zuruecknehmen(this.bp);
		if (result.ok && result.doc) {
			Object.assign(this.bp, result.doc);
			this.notifyPointChanged();
		}
	}

	/**
	 * Übernahme/Freigabe (`abgeschlossen → anstehend`) — Gegenstück zu
	 * `onZuruecknehmen`. Wird vom Diagramm-Pfeil „Zurückholen" ausgelöst (der Pfeil
	 * war zuvor wirkungslos, weil `abgeschlossen → anstehend` keine Plain-Transition
	 * ist; User-Wunsch 2026-06-19). Teilt den Code-Pfad mit dem Toolbar-/Banner-
	 * Knopf (`PointFreigabeService.freigeben`), der den Service-seitigen Confirm +
	 * Toast übernimmt. Berechtigung: `ProjectService.canIfreigebenPoint`.
	 */
	async onFreigeben(): Promise<void> {
		const result = await this.freigabe.freigeben(this.bp);
		if (result.ok && result.doc) {
			Object.assign(this.bp, result.doc);
			this.notifyPointChanged();
		}
	}

	async onTransition(target: PointStatus): Promise<void> {
		// Defensiv: kein Status-Wechsel an nicht zugeordneten Punkten (s. Getter).
		if (!this.isAssignedToActiveProject()) {
			this.toastr.error('Der Punkt ist diesem Projekt nicht zugeordnet.');
			return;
		}
		const ref = this.modalService.open(WorkflowTransitionModalComponent, {
			size: 'lg', scrollable: true
		});
		ref.componentInstance.doc = this.bp;
		ref.componentInstance.targetStatus = target;
		ref.componentInstance.fromStatus = this.currentStatus;
		try {
			const result = await ref.result as { message?: string; forceUnresolved?: boolean };
			const hasUnresolved = result?.forceUnresolved
				? false
				: this.bp.state?.resolved === false;
			const result_ = await this.workflow.transition(this.bp, target, {
				input: target === 'abgelehnt'
					? { rejectionMessage: result?.message }
					: { approvalMessage: result?.message },
				context: {
					hasBlockingDataIssues: this.bp.state?.dataSeverity === ValidationLevel.danger,
					hasUnresolvedComments: hasUnresolved,
					hasDangerComments: hasUnresolvedDangerComments(this.bp)
				}
			});
			if (!result_.ok) {
				this.toastr.error(result_.reason ?? 'Status-Wechsel fehlgeschlagen');
				return;
			}
			if (result_.doc) {
				Object.assign(this.bp, result_.doc);
				this.notifyPointChanged();
			}
			this.toastr.success(`Neuer Status: ${this.currentBadge?.label ?? target}`);
		} catch (_dismissed) {
			// Modal abgebrochen — kein Toast.
		}
	}

	/**
	 * Öffnet das Workflow-Diagramm (Status-Übersicht). Bestätigt der User dort
	 * einen Aktions-Button, läuft der Übergang über denselben Pfad wie die
	 * Transition-Buttons (Begründungs-Modal). Früher im Tabs-Header — jetzt
	 * hier im Hinweise-Tab, wo der Workflow ohnehin lebt.
	 */
	onShowDiagram(): void {
		const ref = this.modalService.open(WorkflowDiagramModalComponent, {
			size: 'lg', scrollable: true
		});
		ref.componentInstance.doc = this.bp;
		// Gehört der Punkt nicht zum aktiven Projekt, ist sein Workflow von hier aus
		// nicht steuerbar — das Diagramm dann nur LESEND zeigen (Rolle 'none' → keine
		// Aktions-Buttons), statt Übergänge anzubieten, die das Ausführungs-Gate
		// anschließend ablehnt (sonst „erlaubt schien, ging dann nicht").
		// Diagramm im Mehrfachrollen-Modus (MR, 2026-06-18): Die Kanten werden
		// gegen die VEREINIGUNG der gehaltenen Rollen aktiviert (kein
		// currentRole/getWorkflowRole-Kollaps) — so ist z. B. die Greifen-Kante
		// `anstehend→bearbeitung` auch für einen Bearbeiter+Kontrolleur aktiv.
		// Nicht dem aktiven Projekt zugeordnete Punkte → leere Flags (read-only).
		ref.componentInstance.roles = this.isAssignedToActiveProject()
			? this.workflow.currentRoleFlags()
			: {};
		ref.componentInstance.isOwnPoint = isOwnPoint(this.bp, this.aus.currentUserId);
		// Sonderkanten ans App-Gate koppeln (sonst leuchten sie beim Admin-Bypass
		// auch dann, wenn die Ausführung sie ablehnt): „Übernahme zurücknehmen"
		// nur bei echter, rücknehmbarer Übernahme; „Zurückholen"/Freigabe nur bei
		// freigebbarem Altpunkt. Spiegelt exakt die Per-Punkt-Knöpfe.
		ref.componentInstance.canRevertTakeover = this.canZuruecknehmen;
		ref.componentInstance.canFreigeben = this.ps.canIfreigebenPoint(this.bp);
		ref.closed.subscribe((value: { targetStatus?: PointStatus } | undefined) => {
			if (!value?.targetStatus) return;
			// Zwei Diagramm-Pfeile sind KEINE Plain-Transitionen (eigener Pfad wie
			// restartWorkflow/reopen) und müssen separat geroutet werden — jeweils
			// gegen ihr Punkt-Gate, damit der Pfeil exakt dem Knopf-Verhalten folgt:
			//   • anstehend → abgeschlossen: „Übernahme zurücknehmen" (Rücknahme-Service)
			//   • abgeschlossen → anstehend: „Zurückholen"/Übernahme (Freigabe-Service)
			if (value.targetStatus === 'abgeschlossen' && this.currentStatus === 'anstehend') {
				if (this.canZuruecknehmen) void this.onZuruecknehmen();
				else this.toastr.error('Diese Übernahme kann nicht zurückgenommen werden.');
				return;
			}
			if (value.targetStatus === 'anstehend' && this.currentStatus === 'abgeschlossen') {
				if (this.ps.canIfreigebenPoint(this.bp)) void this.onFreigeben();
				else this.toastr.error('Dieser Punkt kann von hier nicht freigegeben werden — ggf. „Zurück in die Kontrolle" nutzen.');
				return;
			}
			this.onTransition(value.targetStatus);
		});
	}

	/**
	 * Das Projekt, dem der Punkt gehört, wenn es ein ANDERES als das aktive ist —
	 * sonst `null`. Für den read-only Cross-Projekt-Hinweis im Template.
	 */
	get foreignProject(): { id: string; name: string } | null {
		return foreignProjectOf(this.bp?.header?.projects, this.ps.project());
	}

	/**
	 * Der Name des fremden Projekts — `null`, wenn der Nutzer Projektnamen nicht
	 * sehen darf (Datennutzer, nicht angemeldet; dieselbe Regel wie beim
	 * Sperr-Banner der Detail-Ansicht). Der Hinweis erscheint dann ohne Namen.
	 */
	get fremdesProjektName(): string | null {
		const projekt = this.foreignProject;
		if (!projekt) return null;
		if (shouldHideProjectName(this.aus.isAuthenticated.getValue(), this.aus.isDatennutzer())) return null;
		return projekt.name;
	}

	/**
	 * Wer Kartierer und Aufnahmedatum nicht sehen darf (Datennutzer, nicht
	 * Angemeldete), sieht im Reiter „Hinweise" auch nicht „Zuletzt bearbeitet
	 * … von …" und die Import-Zeile (Nutzer-Befund 06.09.2026, Gastmodus).
	 * Kommentare und Workflow-Verlauf verbergen ihre Namen und Zeitpunkte
	 * selbst (Lib-Bauteile).
	 */
	get personenVerborgen(): boolean {
		return personenangabenVerbergen(this.aus);
	}

	/**
	 * Frozen-Lock (abgeschlossener Fremdpunkt). Der Parent-Banner deckt diesen
	 * Fall bereits ab — der Hinweise-Tab-Banner würde nur doppeln.
	 */
	get isPointFrozen(): boolean {
		return !!this.bp && this.workflow.isFrozen(this.bp);
	}

	/**
	 * Prüft ob der Punkt dem aktiven Projekt zugewiesen ist. Wird noch vom
	 * Workflow-Transitions-Gate (`availableTransitions`) gebraucht — die
	 * generische Zuordnung selbst entfällt im Einfachmodell.
	 */
	isAssignedToActiveProject(): boolean {
		// Single-Source in ProjectService (zuvor 3× inline reimplementiert).
		return this.ps.isPointAssignedToActiveProject(this.bp);
	}

	/**
	 * Gibt die Anzahl der Kommentare zurück.
	 */
	get commentsCount(): number {
		const comments = this.vs.checkerComments();
		return comments ? comments.size : 0;
	}

	/**
	 * Formatiert die letzte Änderung.
	 */
	formatLastModified(lm?: LastModified): string {
		if (!lm) return '';
		const date = new Date(lm.time);
		const dateStr = date.toLocaleDateString('de-DE');
		const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
		return `${dateStr} ${timeStr} von ${lm.user}`;
	}

	/**
	 * Formatiert den Import-Zeitstempel.
	 */
	formatImportTime(timestamp?: number): string {
		if (!timestamp) return '';
		const date = new Date(timestamp);
		const dateStr = date.toLocaleDateString('de-DE');
		const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
		return `${dateStr} ${timeStr}`;
	}

	canComment(cmts: CheckerComments, idx: number) {
		if (!cmts || cmts.resolved || !this.aus.commenting) return false;
		return idx === undefined ? this.aus.commenting : (idx === cmts.comments!.length - 1);
	}

	commentsClass(comments: CheckerComments, prefix = ''): string {
		const rubber = this.aus.pen === 'rubber' ? 'rubber ' : '';
		switch (comments?.type) {
			case ValRes.danger: return `${rubber}${prefix}danger`;
			case ValRes.warning: return `${rubber}${prefix}warning`;
			case ValRes.ok: return `${rubber}${prefix}message`;
			default: return '';
		}
	}

	onCommentsClicked(key: string, value: CheckerComments) {
		const modalRef = this.modalService.open(CommentsModalComponent, { scrollable: true });
		// Tiefe Kopie: Modal mutiert type/resolved direkt am Input — bei Dismiss
		// würde das Original sonst verändert bleiben und beim nächsten Save persistiert.
		const commentsCopy: CheckerComments = {
			...value,
			comments: value.comments.map(c => ({ ...c }))
		};
		modalRef.componentInstance.comments = commentsCopy;
		modalRef.componentInstance.name = key;
		modalRef.closed.subscribe((result?: CheckerComments | 'saved') => {
			// 'saved' = Modal hat bereits gespeichert, nichts mehr tun
			if (result === 'saved') {
				return;
			}
			// Wenn kein Wert zurückgegeben wurde (z.B. bei "Alle löschen"), clearComments aufrufen
			if (!result) {
				this.vs.clearComments(key);
				return;
			}
			// Prüfen ob der Kommentar bereits gelöscht wurde (durch deleteComment in bohrpunkt-comments)
			// In diesem Fall existiert er nicht mehr in der Map und wir sollten nichts tun
			const existing = this.vs.hasComments(key);
			if (!existing) {
				// Kommentar wurde bereits gelöscht - nichts tun
				return;
			}
			// Kommentar existiert noch - aktualisieren
			this.vs.resetComments(key, result);
		})
	}

}
